import {
  Flex,
  Box,
  TextV2 as Text,
  Text as TextOld,
  Colors,
  InputV2,
  SwitchV2,
} from '@pinchenterprisesnpm/friday-ui'
import { faAlarmClock, faDollarSign } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { connect, FormikProps } from 'formik'
import { getBreakValue } from 'ui/components/Admin/Shifts/ShiftEditDuplicate/utils'
import { TimePicker } from 'ui/components/common/TimePicker'
import CustomDropdownV5 from 'features/shift-post-v2/steps/shift-details-step/components/schedule-section/components/custom-dropdown-v5/custom-dropdown-v5'
import { SHIFT_TYPE } from 'models/Shift'
import { shouldShowOTPolicyWarningV2 } from '@src/helpers/shiftHelpers'
import { OTPolicyWarning } from '@src/ui/components/Admin/OTPolicyWarning'
import { RecurringDayPicker } from '@features/shift-post-v2/components'
import { SectionContainer } from '../new-edit-modal.style'
import { INewEditModalFormValues } from '../new-edit-modal.form'
import { IndividualDaysList } from './individual-days-list'
import { PayRateFeedback } from './pay-rate-feedback'
import { SingleDayPicker } from './single-day-picker'
import {
  handlePayRateChange,
  handleHourChange,
  handleBreakChange,
  handlePositionsChange,
} from './form-handlers'

interface IScheduleSectionProps {
  formik?: FormikProps<INewEditModalFormValues>
}

export const ScheduleSection = connect<IScheduleSectionProps>(({ formik }) => {
  const { values, setFieldValue, handleBlur, errors } = formik
  const payRateLimit = values.location.active_overtime_policy?.pay_rate_limit
  const showOTPolicyWarning = payRateLimit
    ? shouldShowOTPolicyWarningV2(values as any, payRateLimit, true)
    : false
  return (
    <SectionContainer>
      <Flex mb='sm' justifyContent='space-between'>
        <Text variant='h6'>Shift Schedule</Text>
        <Flex alignItems='center'>
          <Text mr={10} color='gray'>
            Recurring Shift
          </Text>
          <SwitchV2
            value={values.is_recurring_shift}
            onChange={async (value: boolean) => {
              setFieldValue('is_recurring_shift', value)
              // Automatically set shift type based on recurring toggle
              setFieldValue('type', value ? SHIFT_TYPE.MULTIDAY : SHIFT_TYPE.SINGLEDAY)
              // Reset day selection when switching to recurring
              if (value) {
                await setFieldValue('days', [])
                await setFieldValue('multiday_day_start', undefined)
                await setFieldValue('multiday_day_end', undefined)
                await setFieldValue('date_ids', {})
              }
            }}
          />
        </Flex>
      </Flex>

      {/* Pay rate and unpaid break */}
      <Flex mt='md' alignItems='flex-start' justifyContent='space-between'>
        <Box width='33%'>
          <Flex alignItems='center'>
            <InputV2
              leftElement={<FontAwesomeIcon icon={faDollarSign} size='lg' color={Colors.muted} />}
              label='Hourly Pay Rate'
              placeholder=''
              type='number'
              name='pay_value'
              id='pay_value'
              width='100%'
              value={values.pay_value}
              onChange={(e) => handlePayRateChange(e, formik)}
              onBlur={handleBlur}
              errorMessage={errors.pay_value}
              onKeyDown={(e) => {
                if (['e', 'E', '-', '+'].includes(e.key)) {
                  e.preventDefault()
                }
              }}
            />
          </Flex>
        </Box>
        <Box minWidth='33%'>
          <InputV2
            label='Positions'
            placeholder=''
            type='number'
            name='positions'
            id='positions'
            width='100%'
            value={values.positions}
            onChange={(e) => handlePositionsChange(e, formik)}
            onBlur={handleBlur}
            errorMessage={errors.positions}
          />
        </Box>
        <Box minWidth='33%'>
          <CustomDropdownV5
            topLabel='Unpaid break'
            icon={faAlarmClock}
            width='100%'
            options={getBreakValue()}
            value={
              getBreakValue().find((opt) => opt.value === (values.break || 0)) || getBreakValue()[0]
            }
            onChange={(selected: any) => handleBreakChange(selected, formik)}
            menuPlacement='top'
          />
        </Box>
      </Flex>
      {showOTPolicyWarning && (
        <Box mt='xs'>
          <OTPolicyWarning payRate={payRateLimit ?? 0} />
        </Box>
      )}

      <PayRateFeedback
        payValue={Number(values.pay_value)}
        suggestedPayRate={Number(values.suggested_pay_rate)}
      />

      {/* Date picker and Time picker side by side */}
      <Flex mt='md' gap='md' justifyContent='space-between'>
        {/* Date picker */}
        <Box minWidth='45%'>
          {values.type === SHIFT_TYPE.SINGLEDAY && <SingleDayPicker formik={formik} />}

          {values.type === SHIFT_TYPE.MULTIDAY && <RecurringDayPicker formik={formik as any} />}
        </Box>

        {/* Time picker */}
        <Box minWidth='45%'>
          <Flex gap='md' justifyContent='space-between'>
            <Box>
              <TextOld mt={0} mb='xs' fontWeight='regular'>
                Start Time
              </TextOld>

              <TimePicker
                value={values.hour_start}
                animated
                width='auto'
                height='auto'
                right='auto'
                left='auto'
                onChange={(value) => handleHourChange(value, true, formik)}
              />
            </Box>

            <Box>
              <TextOld mt={0} mb='xs' fontWeight='regular'>
                End Time
              </TextOld>

              <TimePicker
                value={values.hour_end}
                animated
                width='auto'
                height='auto'
                right='auto'
                left='auto'
                onChange={(value) => handleHourChange(value, false, formik)}
              />
            </Box>
          </Flex>
        </Box>
      </Flex>

      {/* Individual days list underneath */}
      {values.type === SHIFT_TYPE.MULTIDAY && (
        <Box mt='md'>
          <IndividualDaysList formik={formik} />
        </Box>
      )}
    </SectionContainer>
  )
})
